import { useCallback, useEffect, useMemo, useState } from "react";
import {
  resolveCatalogReportRange,
  type CatalogReportDateRange,
  type CatalogReportPreset,
} from "@/lib/catalog/report-date-range";
import { useToast } from "@/contexts/ToastContext";

export function useCatalogReportQuery(defaultPreset: CatalogReportPreset = "thisMonth") {
  const [preset, setPreset] = useState<CatalogReportPreset>(defaultPreset);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const range: CatalogReportDateRange = useMemo(
    () => resolveCatalogReportRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  const isCustomRangeReady = preset !== "custom" || (Boolean(customFrom) && Boolean(customTo));

  return {
    preset,
    setPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    range,
    isCustomRangeReady,
  };
}

export function useCatalogReportLoader<T>(
  load: (range: CatalogReportDateRange) => Promise<T>,
  range: CatalogReportDateRange,
  enabled: boolean,
  errorMessage = "Unable to load cataloging stats for the selected period."
) {
  const toast = useToast();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setData(await load(range));
    } catch (err: unknown) {
      console.error(err);
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        errorMessage;
      toast.error(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, errorMessage, load, range, toast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, refresh };
}
